import _, { isNil } from 'lodash';
import { AnyObject, array, ArraySchema, date, number, object, string } from 'yup';

import { DateOfServiceRequirementOverride } from '../enum/dos-requirement-override.enum';
import { getQualityEntryAttachmentMax } from '../functions/get-quality-entry-attachment-max.function';
import { getQualityEntryAttachmentMin } from '../functions/get-quality-entry-attachment-min.function';

const attachmentObject = object({
  data: object({
    fileName: string().required(),
  }).required(),
  entityClass: string().required(),
  entityId: string().required(),
  entityVersion: number().required(),
});

export const attachmentsSchema = array().when(['$config', 'uploads'], ([config, uploads], schema) => {
  return filesSchema(config, schema, uploads);
});

export const uploadsSchema = array().when(['$config', 'attachments'], ([config, attachments], schema) => {
  return filesSchema(config, schema, attachments);
});

function filesSchema(config: any, schema: ArraySchema<any[] | undefined, AnyObject, undefined, ''>, files: any[]) {
  const attachmentMinOverride = config?.attachmentMinOverride as number | undefined;
  const attachmentMaxOverride = config?.attachmentMaxOverride as number | undefined;

  const attachmentsEnabled =
    (config?.programPeriodConfig?.config?.quality?.attachmentsEnabled as boolean | undefined) ?? false;
  const attachmentsRequired =
    (config?.programPeriodConfig?.config?.quality?.attachmentsRequired as boolean | undefined) ?? false;
  const attachmentsLimited =
    (config?.programPeriodConfig?.config?.quality?.attachmentsLimited as boolean | undefined) ?? false;

  const numFiles = files?.length ?? 0;

  const attachmentMin = getQualityEntryAttachmentMin(attachmentsEnabled, attachmentsRequired, attachmentMinOverride);
  const isAttachmentMin = !isNil(attachmentMin);
  const minDiff = (attachmentMin ?? 0) - numFiles;
  const isMinFiles = minDiff <= 0;

  const attachmentMax = getQualityEntryAttachmentMax(attachmentsEnabled, attachmentsLimited, attachmentMaxOverride);
  const isAttachmentMax = !isNil(attachmentMax);
  const maxDiff = (attachmentMax ?? 0) - numFiles;
  const isMaxFiles = maxDiff <= 0;

  let attachmentsMinMax = !isAttachmentMin || isMinFiles ? schema.nullable() : schema.required();

  if (isAttachmentMin) {
    const min = isMinFiles ? 0 : minDiff;
    const message =
      attachmentMin === 1
        ? `Minimum of 1 attachment is required`
        : `Minimum of ${attachmentMin} attachments are required`;
    attachmentsMinMax = attachmentsMinMax.min(min, message);
  }

  if (isAttachmentMax) {
    const max = isMaxFiles ? 0 : maxDiff;
    const message =
      attachmentMax === 1
        ? `Maximum of 1 attachment is allowed`
        : `Maximum of ${attachmentMax} attachments are allowed`;
    attachmentsMinMax = attachmentsMinMax.max(max, message);
  }

  return attachmentsMinMax;
}

export const dateOfServiceSchema = date()
  .transform(normalizeDate)
  .when('$config', ([config], schema) => {
    const { dateOfServiceMin, dateOfServiceMax, dateOfServiceRequirementOverride } = config;
    const min = normalizeDate(dateOfServiceMin);
    const max = normalizeDate(dateOfServiceMax);

    const isExplicitlyOptionalOrDisabled =
      dateOfServiceRequirementOverride === DateOfServiceRequirementOverride.Optional ||
      dateOfServiceRequirementOverride === DateOfServiceRequirementOverride.Disabled;

    let dateOfServiceMinMax = !isExplicitlyOptionalOrDisabled
      ? schema.required().typeError('Date of Service is required')
      : schema.nullable().notRequired();

    const hasMinMaxConstraints = min || max;

    if (dateOfServiceRequirementOverride !== DateOfServiceRequirementOverride.Disabled && hasMinMaxConstraints) {
      if (min) {
        dateOfServiceMinMax = dateOfServiceMinMax.min(
          min,
          `Date of Service must be no earlier than ${min.toLocaleDateString()}`
        );
      }

      if (max) {
        dateOfServiceMinMax = dateOfServiceMinMax.max(
          max,
          `Date of Service must be no later than ${max.toLocaleDateString()}`
        );
      }
    }

    return dateOfServiceMinMax;
  });

export const formQualityEntrySchema = object().shape(
  {
    uploads: uploadsSchema,
    attachments: attachmentsSchema.of(attachmentObject),
    provider: object().when(['$config'], ([config], schema) => {
      return config?.qualitySectionConfig?.data?.providerEnabled && config?.qualitySectionConfig?.data?.providerRequired
        ? schema.required().typeError('Provider is required')
        : schema.nullable().notRequired();
    }),
    procedure: object({
      code: string().when(['$config'], ([config], schema) =>
        config?.programPeriodConfig?.config?.quality?.optionSelectionLimitEnabled
          ? schema.required().oneOf(config?.validProcedureCodes)
          : schema.required()
      ),
      codeType: string().required(),
      inputRegex: string().nullable().notRequired(),
      validatorMessage: string().nullable().notRequired(),
      name: string().required(),
      units: string().nullable().notRequired(),
      providerSpecialty: string().notRequired(),
    }).required(),
    dateOfService: dateOfServiceSchema,
    procedureValue: string()
      .nullable()
      .when(['procedure'], ([procedure], schema) => {
        return procedure?.inputRegex
          ? schema.required('Procedure Value is required').matches(procedure.inputRegex, procedure.validatorMessage)
          : schema.notRequired();
      }),
  },
  [['attachments', 'uploads']]
);

function normalizeDate(dateOfService?: Date) {
  return dateOfService ? new Date(dateOfService.toDateString()) : undefined;
}
